// src/contexts/FollowsContext.jsx
// Load all follows ONCE, share across all FollowButtons
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { safeStorage } from '../utils/safeStorage';

const FollowsContext = createContext();

export function FollowsProvider({ children }) {
  const [follows, setFollows] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Initialize from cache so the button is ready before GweffIT fires its event
  const [currentUserId, setCurrentUserId] = useState(() => {
    try {
      return JSON.parse(safeStorage.get('gweffit_user_cache') || 'null')?.id || null;
    } catch { return null; }
  });

  // React to login/logout events dispatched by GweffIT
  useEffect(() => {
    function onUserChanged(e) {
      setCurrentUserId(e.detail?.userId || null);
    }
    window.addEventListener('gweffit:user-changed', onUserChanged);
    return () => window.removeEventListener('gweffit:user-changed', onUserChanged);
  }, []);

  // Load ALL follows once when app starts
  useEffect(() => {
    if (!currentUserId) {
      setLoading(false);
      return;
    }

    async function loadFollows() {
      try {
        const { data, error } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', currentUserId);

        if (error) {
          console.error('Error loading follows:', error);
        } else if (data) {
          // Store as Set for fast lookups
          const followSet = new Set(data.map(f => f.following_id));
          setFollows(followSet);
        }
      } catch (err) {
        console.error('Error loading follows:', err);
      } finally {
        setLoading(false);
      }
    }

    loadFollows();
  }, [currentUserId]);

  // Follow a user
  async function followUser(targetUserId) {
    if (!currentUserId) return false;

    try {
      const { error } = await supabase
        .from('follows')
        .insert({ 
          follower_id: currentUserId, 
          following_id: targetUserId 
        });

      if (error) throw error;

      // Update local state immediately
      setFollows(prev => new Set([...prev, targetUserId]));
      console.log('[follow_action_result]', {
        action: 'follow',
        ok: true,
        target_user_id: targetUserId,
      });
      try {
        window.trackEvent?.('follow_action_result', {
          action: 'follow',
          ok: true,
          target_user_id: targetUserId,
        });
      } catch {}
      return true;
    } catch (err) {
      console.error('Error following user:', err);
      console.warn('[follow_action_result]', {
        action: 'follow',
        ok: false,
        target_user_id: targetUserId,
        error: err?.message || String(err),
      });
      return false;
    }
  }

  // Unfollow a user
  async function unfollowUser(targetUserId) {
    if (!currentUserId) return false;

    try {
      const { error } = await supabase
        .from('follows')
        .delete()
        .match({ 
          follower_id: currentUserId, 
          following_id: targetUserId 
        });

      if (error) throw error;

      // Update local state immediately
      setFollows(prev => {
        const newSet = new Set(prev);
        newSet.delete(targetUserId);
        return newSet;
      });
      console.log('[follow_action_result]', {
        action: 'unfollow',
        ok: true,
        target_user_id: targetUserId,
      });
      try {
        window.trackEvent?.('follow_action_result', {
          action: 'unfollow',
          ok: true,
          target_user_id: targetUserId,
        });
      } catch {}
      return true;
    } catch (err) {
      console.error('Error unfollowing user:', err);
      console.warn('[follow_action_result]', {
        action: 'unfollow',
        ok: false,
        target_user_id: targetUserId,
        error: err?.message || String(err),
      });
      return false;
    }
  }

  // Check if following a user (instant, no database call!)
  function isFollowing(targetUserId) {
    return follows.has(targetUserId);
  }

  const value = {
    follows,
    loading,
    followUser,
    unfollowUser,
    isFollowing,
    currentUserId
  };

  return (
    <FollowsContext.Provider value={value}>
      {children}
    </FollowsContext.Provider>
  );
}

// Hook to use follows context
export function useFollows() {
  const context = useContext(FollowsContext);
  if (!context) {
    throw new Error('useFollows must be used within FollowsProvider');
  }
  return context;
}
